import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { firstValueFrom, lastValueFrom, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RateManagerService {
  protected endpoint?: string;

  constructor(private apiService: ApiService) {
    this.endpoint = 'rate-manager';
  }

  cleanPayload(rawPayload: any) {
    return Object.fromEntries(
      Object.entries(rawPayload).filter(
        ([
          _,
          v,
        ]) => v !== null,
      ),
    );
  }

  getRates(payload: any = {}) {
    payload = this.cleanPayload(payload);

    const isDownloadCsv = payload.download_csv === true;

    if (isDownloadCsv) {
      const options = { responseType: 'text' as const };
      return this.apiService.get(`${this.endpoint}/provider/rates`, payload, false, options);
    }

    return this.apiService.get(`${this.endpoint}/provider/rates`, payload, true);
  }

  getRatesAgGrid(payload: any = {}) {
    payload = this.cleanPayload(payload);

    const isDownloadCsv = payload.download_csv === true;

    if (isDownloadCsv) {
      const options = { responseType: 'text' as const };
      return this.apiService.get(`${this.endpoint}/provider/rates/ag-grid`, payload, false, options);
    }

    return this.apiService.get(`${this.endpoint}/provider/rates/ag-grid`, payload, true);
  }

  createRate(payload: any = {}) {
    payload = this.cleanPayload(payload);
    return this.apiService.post(`${this.endpoint}/provider/rates`, payload);
  }

  bulkCreateRate(formData: FormData) {
    // Send FormData directly; do NOT clean it
    return this.apiService.uploadFile(`${this.endpoint}/provider/upload-rate`, formData);
  }

  async getProviderRatesCategories(payload: object = {}, skipInterceptor: boolean = false) {
    try {
      payload = this.cleanPayload(payload);
      return await firstValueFrom(this.apiService.get(`${this.endpoint}/provider/rates-categories`, payload, skipInterceptor));
    } catch (error) {
      console.error('RateManagerService.getProviderRatesCategories error:', error);
      throw error;
    }
  }

  async getLocation(payload: object = {}, skipInterceptor: boolean = false) {
    payload = this.cleanPayload(payload);
    return await firstValueFrom(this.apiService.get(`${this.endpoint}/provider/rates-locations`, payload, skipInterceptor));
  }

  async getProviderRatesSubCategories(id: number, payload: object = {}) {
    payload = this.cleanPayload(payload);
    return await firstValueFrom(this.apiService.getById(`${this.endpoint}/provider/rates-sub-categories`, payload, id));
  }

  updateRateStatus(rates: { route_rate_ids: number[]; updates: Record<string, any> }[]) {
    return this.apiService.put(`${this.endpoint}/provider/rates/bulk-update`, rates);
  }

  bulkUpdateRates(rates: { route_rate_ids: number[]; updates: Record<string, any> }[]) {
    return this.apiService.put(`${this.endpoint}/provider/rates/bulk-update`, rates);
  }

  async getVehicles(payload: object = {}) {
    payload = this.cleanPayload(payload);
    return await firstValueFrom(this.apiService.get(`${this.endpoint}/provider/rates-vehicles`, payload));
  }

  deleteRate(ids: number[]) {
    return this.apiService.delete(`${this.endpoint}/provider/rates/bulk-delete`, { ids });
  }

  downloadRateTemplate(categoryId: number) {
    const options = { responseType: 'text' as const };
    return this.apiService.get(`${this.endpoint}/provider/download-rate-template/${categoryId}`, {}, false, options);
  }

  // ─── Job-based Async Upload Flow ────────────────────────────────────────────

  /**
   * Step 1: Upload CSV file. Returns a job_id for polling.
   * POST /upload-rate
   */
  uploadRateCsv(formData: FormData): Observable<any> {
    return this.apiService.uploadFile(`${this.endpoint}/provider/upload-rate`, formData, true);
  }

  /**
   * Step 2: Poll job status until validation completes, or fetch a page of
   * preview rows once ready.
   * GET /upload-jobs/{jobId}?page=&page_size=&rate_category_id=
   */
  getUploadJobStatus(
    jobId: string | number,
    params: {
      page?: number;
      page_size?: number;
      rate_category_id?: number | null;
    } = {},
  ): Observable<any> {
    const payload = this.cleanPayload(params);
    return this.apiService.get(`${this.endpoint}/provider/upload-jobs/${jobId}`, payload, true);
  }

  /**
   * Step 3: Commit validated job to the database.
   * POST /upload-jobs/{jobId}/commit
   */
  commitUploadJob(
    jobId: string | number,
    payload: {
      rows: Record<string, any>[];
      columns: string[];
      create_zone?: boolean;
      skip_errors?: boolean;
    } = { rows: [], columns: [], create_zone: true, skip_errors: true },
  ): Observable<any> {
    return this.apiService.post(`${this.endpoint}/provider/upload-jobs/${jobId}/commit`, payload);
  }

  /**
   * Get the latest upload job for the provider.
   * GET /upload-jobs
   */
  getLatestUploadJob(
    params: {
      provider_id?: number;
      rate_category_id?: number;
    } = {},
  ): Observable<any> {
    const payload = this.cleanPayload(params);
    return this.apiService.get(`${this.endpoint}/provider/upload-jobs`, payload, true);
  }

  /**
   * List all upload jobs for the provider (paginated).
   * GET /upload-jobs/list
   */
  getUploadJobsList(
    params: {
      provider_id?: number;
      page?: number;
      page_size?: number;
      rate_category_id?: number;
    } = {},
  ): Observable<any> {
    const payload = this.cleanPayload(params);
    return this.apiService.get(`${this.endpoint}/provider/upload-jobs/list`, payload, true);
  }

  /**
   * Delete/cancel an upload job.
   * DELETE /upload-jobs/{jobId}
   */
  deleteUploadJob(jobId: string | number): Observable<any> {
    return this.apiService.delete(`${this.endpoint}/provider/upload-jobs/${jobId}`);
  }
}
